package songwritersnotebook.app.utils

import kotlinx.browser.window

actual class PreferencesManager actual constructor() {

    private fun getBoolean(key: String, defaultValue: Boolean): Boolean {
        return window.localStorage.getItem(key)?.toBoolean() ?: defaultValue
    }

    private fun setBoolean(key: String, value: Boolean) {
        window.localStorage.setItem(key, value.toString())
    }

    private fun getString(key: String, defaultValue: String): String {
        return window.localStorage.getItem(key) ?: defaultValue
    }

    private fun setString(key: String, value: String) {
        window.localStorage.setItem(key, value)
    }

    private fun getLong(key: String, defaultValue: Long): Long {
        return window.localStorage.getItem(key)?.toLongOrNull() ?: defaultValue
    }

    private fun setLong(key: String, value: Long) {
        window.localStorage.setItem(key, value.toString())
    }

    private fun getInt(key: String, defaultValue: Int): Int {
        return window.localStorage.getItem(key)?.toIntOrNull() ?: defaultValue
    }

    private fun setInt(key: String, value: Int) {
        window.localStorage.setItem(key, value.toString())
    }

    actual var hasSeenTutorial: Boolean
        get() = getBoolean("has_seen_tutorial", false)
        set(value) = setBoolean("has_seen_tutorial", value)

    actual var lastSongEditTimestamp: Long
        get() = getLong("last_song_edit_timestamp", 0L)
        set(value) = setLong("last_song_edit_timestamp", value)

    actual var downloadAllAudio: Boolean
        get() = getBoolean("download_all_audio", false)
        set(value) = setBoolean("download_all_audio", value)

    actual var showSectionHeaders: Boolean
        get() = getBoolean("show_section_headers", true)
        set(value) = setBoolean("show_section_headers", value)

    actual fun getShowPianoInfo(songId: String): Boolean = getBoolean("show_piano_info_$songId", false)
    actual fun setShowPianoInfo(songId: String, show: Boolean) = setBoolean("show_piano_info_$songId", show)

    actual fun getShowGuitarInfo(songId: String): Boolean = getBoolean("show_guitar_info_$songId", false)
    actual fun setShowGuitarInfo(songId: String, show: Boolean) = setBoolean("show_guitar_info_$songId", show)

    actual var chordDisplayMode: String
        get() = getString("chord_display_mode", "auto")
        set(value) = setString("chord_display_mode", value)

    actual var showTabs: Boolean
        get() = getBoolean("show_tabs", true)
        set(value) = setBoolean("show_tabs", value)

    actual var syllableCountEnabled: Boolean
        get() = getBoolean("syllable_count_enabled", true)
        set(value) = setBoolean("syllable_count_enabled", value)

    actual var rhymingEnabled: Boolean
        get() = getBoolean("rhyming_enabled", true)
        set(value) = setBoolean("rhyming_enabled", value)

    actual var showSlantRhymes: Boolean
        get() = getBoolean("show_slant_rhymes", false)
        set(value) = setBoolean("show_slant_rhymes", value)

    actual var endOfLineOnly: Boolean
        get() = getBoolean("end_of_line_only", false)
        set(value) = setBoolean("end_of_line_only", value)

    actual var dailyReminderEnabled: Boolean
        get() = window.localStorage.getItem("daily_reminder_enabled")?.toBoolean() ?: true
        set(value) { window.localStorage.setItem("daily_reminder_enabled", value.toString()) }

    actual var newsAndUpdatesEnabled: Boolean
        get() = window.localStorage.getItem("news_and_updates_enabled")?.toBoolean() ?: true
        set(value) { window.localStorage.setItem("news_and_updates_enabled", value.toString()) }

    actual var hasSeededSampleSongs: Boolean
        get() = getBoolean("has_seeded_sample_songs", false)
        set(value) = setBoolean("has_seeded_sample_songs", value)

    actual var lastAppLaunchTimestamp: Long
        get() = getLong("last_app_launch_timestamp", 0L)
        set(value) = setLong("last_app_launch_timestamp", value)

    actual var hasPromptedForNotifications: Boolean
        get() = getBoolean("has_prompted_for_notifications", false)
        set(value) = setBoolean("has_prompted_for_notifications", value)

    actual var appUsageStreak: Int
        get() = getInt("app_usage_streak", 0)
        set(value) = setInt("app_usage_streak", value)

    actual var isLeftHanded: Boolean
        get() = getBoolean("is_left_handed", false)
        set(value) = setBoolean("is_left_handed", value)

    actual var isDarkMode: Boolean
        get() = getBoolean("is_dark_mode", true)
        set(value) = setBoolean("is_dark_mode", value)

    actual var harmonyConfirmationRequired: Boolean
        get() = getBoolean("harmony_confirmation_required", true)
        set(value) = setBoolean("harmony_confirmation_required", value)

    actual var harmonyVoiceEnabled: Boolean
        get() = getBoolean("harmony_voice_enabled", true)
        set(value) = setBoolean("harmony_voice_enabled", value)

    actual var textSize: String
        get() = getString("text_size", "medium")
        set(value) = setString("text_size", value)

    actual var deviceId: String
        get() {
            var id = window.localStorage.getItem("device_id")
            if (id == null || id.isBlank()) {
                id = com.benasher44.uuid.uuid4().toString()
                window.localStorage.setItem("device_id", id)
            }
            return id
        }
        set(value) = window.localStorage.setItem("device_id", value)

    actual var hasDismissedMobileAppCTA: Boolean
        get() = getBoolean("has_dismissed_mobile_app_cta", false)
        set(value) = setBoolean("has_dismissed_mobile_app_cta", value)
}
